import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import VirtualBatteryPercentageSlider from "../../VirtualBatteryPercentageSlider";
import { BatteryDistributionPreview } from "../BatteryDistributionPreview/BatteryDistributionPreview";
import { useContracts } from "@/components/Contracts/hooks/useContracts";
import { useTranslation } from "react-i18next";
import { useVirtualBatteries } from "../../hooks/useVirtualBatteries";


interface ChangeBatteryPercentageDialogParams {
  open: boolean;
  polissaBateriaId: number;
  onClose: () => void;
}

/**
 * Feature component
 *
 * Responsible for rendering a dialog with a slider to edit percentage values related to the distribution of virtual battery percentages
 *
 * - Delegate the rendering of percentage distribution to the `BatteryDistributionPreview` component.
 *
 */
const ChangeBatteryPercentageDialog: FC<ChangeBatteryPercentageDialogParams> = ({
  open, 
  polissaBateriaId, 
  onClose
}) => {
  const { t } = useTranslation();
  const { currentContract, fetchContracts } = useContracts("electricity");
  const { 
    bateriaVirtualPercentages, 
    error,
    isFetchingBateriaVirtualPercentages,
    isWaitingChangeRequest,
    fetchPercentages,
    // getBatteriesDistribution,
    getVirtualBatteryFromId,
    setBatteryPercentage
  } = useVirtualBatteries();

  const [ percentatge, setPercentatge ] = useState<number>(0);

  const serverBateriaVirtual = bateriaVirtualPercentages?.find((e) => e.bateria_id === polissaBateriaId);
  const currentVirtualBattery = getVirtualBatteryFromId(polissaBateriaId);
  // const batteriesDistribution = getBatteriesDistribution();

  useEffect(() => {
    if (currentVirtualBattery) {
      setPercentatge(currentVirtualBattery.pes_percentatge);
      fetchPercentages(currentVirtualBattery.bateria.id);
    }
  }, [currentVirtualBattery?.id, open]);

  const handleSubmit = () => {
    setBatteryPercentage(currentContract.name, currentVirtualBattery!.bateria.id, percentatge)
    .then((a) => {
      fetchPercentages(currentVirtualBattery!.bateria.id);
      fetchContracts(); // TODO: Aixo us workaround que tard o dhora shaura de canviar, no pot ser que cada vegada hagi de fer el fetch de contracts, per actualitzar els valors d'aquest sol contracte, es absurd, pero per ara ho fem aixi, la tasca sen va massa de mare sino.
    })
    .catch(err => {
      setPercentatge(serverBateriaVirtual!.percentage);
    })
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        {t('common:text.virtual_battery_modify_percentage')}
      </DialogTitle>
      <DialogContent style={{overflow: "hidden"}}>
        {error && <Alert severity="error">{ t('common:text.message_cant_do_operation') }</Alert>}
        {t('common:text.virtual_battery_notice')}
        <VirtualBatteryPercentageSlider
          // style={{marginTop: "2rem"}}
          value={percentatge}
          aria-labelledby="discrete-slider"
          step={1}
          min={0}
          max={100}
          onChange={(newValue) => setPercentatge(newValue)}
          disabled={isWaitingChangeRequest}
        />
        { (!isFetchingBateriaVirtualPercentages && !bateriaVirtualPercentages) ? null : <>
          <Typography variant="h6">
            {t("common:text.battery_distribution_preview")}:
          </Typography>
          <BatteryDistributionPreview
            newPercentage={percentatge}
            selectedBatteryId={currentVirtualBattery!.bateria.id}
            expectedAction="changePercentage"
          />
          </>
        }
      </DialogContent>
      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose} disabled={false}>
          {t('common:text.online_send_close')}
        </Button>
        <Button
          key={'send'}
          variant={'text'}
          color={'primary'}
          onClick={handleSubmit}
          disabled={isWaitingChangeRequest || serverBateriaVirtual?.percentage == percentatge}
        >
          { isWaitingChangeRequest ? <CircularProgress color="secondary" style={{ marginRight: 10 }} size={25} /> : null }
          { t('common:text.contract_modification') }
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeBatteryPercentageDialog;
